import { ChevronDown } from '@carbon/icons-react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { Menu, type MenuEntry } from '@/ui';
import { useServices, useSession } from '../AppServicesContext';
import type { SessionMembership } from '../session/types';
import s from '../layouts/Layout.module.css';

export function useMembershipLabel() {
  const { t } = useTranslation('shell');
  return {
    name: (membership: SessionMembership) => (membership.kind === 'personal' ? t('clinic.personal') : membership.clinicName || t('clinic.unnamed')),
    roles: (membership: SessionMembership) => membership.roles.map((role) => t(`roles.${role}`)).join(', '),
  };
}

/** Переключатель клиники (ПОРТ-16): один вход, несколько клиник, данные не смешиваются. */
export function ClinicSwitcher() {
  const { t } = useTranslation('shell');
  const { session } = useServices();
  const { state, membership } = useSession();
  const navigate = useNavigate();
  const label = useMembershipLabel();
  const memberships = state.user?.memberships ?? [];
  if (!membership) return null;

  const content = (
    <>
      <span className={s.clinicText}>
        <span className={s.clinicName}>{label.name(membership)}</span>
        <span className={s.clinicRoles}>{label.roles(membership)}</span>
      </span>
    </>
  );

  if (memberships.length < 2) {
    return (
      <div className={`${s.clinicButton} ${s.clinicStatic}`} aria-label={t('clinic.current', { name: label.name(membership) })} role="group">
        {content}
      </div>
    );
  }

  const items: MenuEntry[] = [
    { type: 'group', id: 'title', label: t('clinic.switchTitle') },
    ...memberships.map<MenuEntry>((item) => ({
      type: 'radio',
      id: item.clinicId,
      label: `${label.name(item)} · ${label.roles(item)}`,
      checked: item.clinicId === membership.clinicId,
      onSelect: () => {
        if (item.clinicId === membership.clinicId) return;
        session.switchClinic(item.clinicId);
        navigate('/', { replace: true });
      },
    })),
  ];

  return (
    <Menu
      placement="bottom-start"
      aria-label={t('clinic.switchTitle')}
      items={items}
      trigger={(props) => (
        <button type="button" className={s.clinicButton} aria-label={t('clinic.switchLabel', { name: label.name(membership) })} {...props}>
          {content}
          <ChevronDown size={16} aria-hidden="true" />
        </button>
      )}
    />
  );
}
